import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createJobPosting, updateJobPosting } from '../../store/slices/jobPostingSlice';
import { useTheme } from '../../contexts/ThemeContext';
import { JobPosting, CreateJobPostingData } from '../../services/jobPostingApi';

interface JobPostingFormProps {
    open: boolean;
    jobPosting: JobPosting | null;
    onClose: () => void;
    onSuccess: () => void;
}

const JobPostingForm: React.FC<JobPostingFormProps> = ({ open, jobPosting, onClose, onSuccess }) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    
    const { loading, error } = useAppSelector((state) => state.jobPostings);

    // Form state
    const [formData, setFormData] = useState<CreateJobPostingData>({
        title: '',
        description: '',
        location: '',
        job_type: '',
        required_experience: '',
        salary: '',
        requirements: '',
        responsibilities: '',
        benefits: '',
        is_active: true
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const jobTypes = [
        'Full-time',
        'Part-time',
        'Contract',
        'Internship',
        'Freelance',
        'Temporary'
    ];

    // Reset form when job posting changes
    useEffect(() => {
        if (jobPosting) {
            setFormData({
                title: jobPosting.title,
                description: jobPosting.description,
                location: jobPosting.location || '',
                job_type: jobPosting.job_type || '',
                required_experience: jobPosting.required_experience || '',
                salary: jobPosting.salary || '',
                requirements: jobPosting.requirements || '',
                responsibilities: jobPosting.responsibilities || '',
                benefits: jobPosting.benefits || '',
                is_active: jobPosting.is_active
            });
        } else {
            setFormData({
                title: '',
                description: '',
                location: '',
                job_type: '',
                required_experience: '',
                salary: '',
                requirements: '',
                responsibilities: '',
                benefits: '',
                is_active: true
            });
        }
        setErrors({});
    }, [jobPosting]);

    const handleInputChange = (field: keyof CreateJobPostingData, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Job title is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Job description is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (jobPosting) {
                await dispatch(updateJobPosting({ id: jobPosting.id, data: formData })).unwrap();
            } else {
                await dispatch(createJobPosting(formData)).unwrap();
            }
            
            onSuccess();
        } catch (error: any) {
            console.error('Error saving job posting:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!loading && !isSubmitting) {
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    minHeight: isMobile ? '100vh' : 'auto'
                }
            }}
        >
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <DialogTitle sx={{ 
                    p: isMobile ? 2 : 3, 
                    mb:2,
                    pb: isMobile ? 1 : 2,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {jobPosting ? 'Edit Job Posting' : 'Add New Job Posting'}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading || isSubmitting}
                        sx={{ minWidth: 'auto', p: 1 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 3 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Job Title *"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                error={!!errors.title}
                                helperText={errors.title}
                                disabled={loading || isSubmitting}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Job Description *"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                error={!!errors.description}
                                helperText={errors.description}
                                disabled={loading || isSubmitting}
                                multiline
                                rows={4}
                                required
                                placeholder="Provide a detailed description of the job role, responsibilities, and requirements..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={formData.location}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                disabled={loading || isSubmitting}
                                placeholder="e.g., Karachi, Pakistan"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Job Type</InputLabel>
                                <Select
                                    value={formData.job_type}
                                    onChange={(e) => handleInputChange('job_type', e.target.value)}
                                    label="Job Type"
                                    disabled={loading || isSubmitting}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">Select Job Type</MenuItem>
                                    {jobTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Required Experience"
                                value={formData.required_experience}
                                onChange={(e) => handleInputChange('required_experience', e.target.value)}
                                disabled={loading || isSubmitting}
                                placeholder="e.g., 2-3 years in software development"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Salary"
                                value={formData.salary}
                                onChange={(e) => handleInputChange('salary', e.target.value)}
                                disabled={loading || isSubmitting}
                                placeholder="e.g., $50,000 - $70,000 annually"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Requirements"
                                value={formData.requirements}
                                onChange={(e) => handleInputChange('requirements', e.target.value)}
                                disabled={loading || isSubmitting}
                                multiline
                                rows={3}
                                placeholder="List the key requirements and qualifications for this position..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Responsibilities"
                                value={formData.responsibilities}
                                onChange={(e) => handleInputChange('responsibilities', e.target.value)}
                                disabled={loading || isSubmitting}
                                multiline
                                rows={3}
                                placeholder="Describe the main responsibilities and duties for this role..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Benefits"
                                value={formData.benefits}
                                onChange={(e) => handleInputChange('benefits', e.target.value)}
                                disabled={loading || isSubmitting}
                                multiline
                                rows={3}
                                placeholder="List the benefits and perks offered with this position..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                        disabled={loading || isSubmitting}
                                    />
                                }
                                label="Active Job Posting"
                            />
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                Active job postings will be visible to potential candidates
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ 
                    p: isMobile ? 2 : 3, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 1,
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading || isSubmitting}
                        fullWidth={isMobile}
                        sx={{ 
                            color: theme.palette.text.secondary,
                            borderRadius: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || isSubmitting}
                        fullWidth={isMobile}
                        sx={{
                            bgcolor: theme.palette.secondary.main,
                            borderRadius: 3,
                            '&:hover': {
                                bgcolor: theme.palette.secondary.dark,
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            jobPosting ? 'Update Job Posting' : 'Create Job Posting'
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default JobPostingForm;
